import * as React from 'react';
import { useRouter } from 'next/router';
import { useTranslate } from '@tolgee/react';

import { EntitiesEnum } from '~/shared/auth/permissions';
import {
  CheersHubIcon,
  OtherAppsIcon,
  OurAppsIcon
} from '~/shared/components/icons';

import { useAbility } from './AbilityContext';

export type RouteObject = {
  key: string;
  title: string;
  path: string;
  icon?: React.ReactNode;
  routeComponents?: RouteObject[];
};

type NavContextData = {
  isAppMenuOpen: boolean;
  subMenuOpen?: RouteObject | null;
  subMenuStack: RouteObject[];
  menuHeaderItem: RouteObject;
  menuItems: RouteObject[];
  footerItems: RouteObject[];
  toggleAppMenu: () => void;
  toggleSubMenu: (item: RouteObject | null) => void;
  pushSubMenu: (item: RouteObject) => void;
  popSubMenu: () => void;
  checkIfIsActive: (menuRoute: string, subitems?: RouteObject[]) => boolean;
};

const NavContext = React.createContext({} as NavContextData);

type NavProviderProps = {
  children: React.ReactNode;
  menuRoutes: RouteObject[];
  footerRoutes: RouteObject[];
};

export function NavProvider({
  children,
  menuRoutes,
  footerRoutes
}: NavProviderProps) {
  const { t } = useTranslate();
  const ability = useAbility();

  const router = useRouter();

  const [isAppMenuOpen, setIsAppMenuOpen] = React.useState(false);
  const [subMenuOpen, setSubmenuOpen] = React.useState<RouteObject | null>();
  const [subMenuStack, setSubMenuStack] = React.useState<RouteObject[]>([]);

  const toggleSubMenu = React.useCallback((item: RouteObject | null) => {
    setSubmenuOpen(item?.routeComponents?.length ? item : null);
  }, []);

  const pushSubMenu = React.useCallback((item: RouteObject) => {
    if (item.routeComponents?.length) {
      setSubMenuStack(prev => [...prev, item]);
    }
  }, []);

  const popSubMenu = React.useCallback(() => {
    setSubMenuStack(prev => {
      const newStack = [...prev];
      newStack.pop();
      return newStack;
    });
  }, []);

  const toggleAppMenu = React.useCallback(() => {
    setIsAppMenuOpen(state => !state);
    // Reset submenu stack when closing menu
    setSubMenuStack([]);
  }, []);

  const checkIfIsActive = React.useCallback(
    (menuRoute: string, subitems?: RouteObject[]) => {
      if (!menuRoute) {
        return false;
      }

      const currentAsPath = router.asPath.split('#')[0];

      if (subitems?.length) {
        const subRouteActive = subitems.some(sub => currentAsPath === sub.path);
        if (subRouteActive) {
          return true;
        }
      }

      if (menuRoute.includes('?')) {
        return currentAsPath === menuRoute;
      }

      const [currentPath] = currentAsPath.split('?');
      const [menuPath] = menuRoute.split('?');
      return currentPath === menuPath;
    },
    [router.asPath]
  );

  const ourApps: RouteObject[] = React.useMemo(() => {
    const values: RouteObject[] = [
      {
        key: 'compensation',
        title: t('menu.ourApps.compensation', 'Compensation'),
        path: process.env.NEXT_PUBLIC_MY_COMPENSATION_URL || '',
        icon: <OtherAppsIcon />
      },
      {
        key: 'northstar',
        title: t('menu.ourApps.northstar', 'NorthStar'),
        path: process.env.NEXT_PUBLIC_MY_TARGETS_URL || '',
        icon: <OtherAppsIcon />
      },
      {
        key: 'opr',
        title: t('menu.ourApps.opr', 'OPR'),
        path: process.env.NEXT_PUBLIC_OPR_HOME_URL || '',
        icon: <OtherAppsIcon />
      },
      {
        key: 'workday',
        title: t('menu.ourApps.workday', 'Workday'),
        path: process.env.NEXT_PUBLIC_WORKDAY_URL || '',
        icon: <OtherAppsIcon />
      }
    ];

    if (ability.can('view', EntitiesEnum.Cheershub)) {
      values.unshift({
        key: 'cheersHub',
        title: t('menu.ourApps.cheersHub', 'CheersHub'),
        path: process.env.NEXT_PUBLIC_CHEERSHUB_URL || '',
        icon: <CheersHubIcon />
      });
    }

    return values;
  }, [ability, t]);

  const providerValue = React.useMemo(
    () => ({
      isAppMenuOpen,
      subMenuOpen,
      subMenuStack,
      menuItems: menuRoutes,
      footerItems: footerRoutes,
      menuHeaderItem: {
        key: 'our_apps',
        title: t('menu.our_apps', 'Our Apps'),
        path: '/',
        icon: <OurAppsIcon />,
        routeComponents: ourApps
      },
      toggleAppMenu,
      toggleSubMenu,
      pushSubMenu,
      popSubMenu,
      checkIfIsActive
    }),
    [
      isAppMenuOpen,
      subMenuOpen,
      subMenuStack,
      menuRoutes,
      footerRoutes,
      t,
      ourApps,
      checkIfIsActive,
      toggleAppMenu,
      toggleSubMenu,
      pushSubMenu,
      popSubMenu
    ]
  );

  return (
    <NavContext.Provider value={providerValue}>{children}</NavContext.Provider>
  );
}

export function useNav() {
  const context = React.useContext(NavContext);

  if (!context) {
    throw new Error('useNav must be used within a NavProvider');
  }

  return context;
}
